import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { AppShell } from "@/components/app-shell";
import { requireRole } from "@/lib/session";
import { useParentContext } from "@/lib/parent-context";
import { supabase } from "@/integrations/supabase/client";
import { ClipboardList, Loader2, FileWarning, CheckCircle2 } from "lucide-react";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/parent/homework")({
  ssr: false,
  beforeLoad: () => requireRole("parent"),
  head: () => ({ meta: [{ title: "Homework — MAKAI" }] }),
  component: HomeworkPage,
});

interface Row {
  childName: string;
  childId: string;
  id: string;
  title: string;
  subject: string;
  due: string | null;
  status: "submitted" | "graded" | "pending" | "overdue";
  score: number | null;
  max: number | null;
}

function HomeworkPage() {
  const { ctx, loading } = useParentContext();
  const [rows, setRows] = useState<Row[]>([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!ctx) return;
    let cancelled = false;
    (async () => {
      setBusy(true);
      const all: Row[] = [];
      for (const c of ctx.children) {
        if (!c.streamId) continue;
        const { data: assigns } = await supabase
          .from("assignments")
          .select("id, title, due_at, subject_id, max_score")
          .eq("stream_id", c.streamId)
          .eq("status", "published")
          .order("due_at", { ascending: true });
        const ids = (assigns ?? []).map((a) => a.id);
        let subs: Record<string, { status: string; score: number | null }> = {};
        if (ids.length) {
          const { data: ss } = await supabase
            .from("assignment_submissions")
            .select("assignment_id, status, score")
            .eq("learner_id", c.learnerId)
            .in("assignment_id", ids);
          for (const s of ss ?? []) subs[s.assignment_id] = { status: s.status, score: s.score };
        }
        const subjectIds = Array.from(new Set((assigns ?? []).map((a) => a.subject_id).filter(Boolean)));
        const subjectMap: Record<string, string> = {};
        if (subjectIds.length) {
          const { data: subj } = await supabase.from("subjects_catalog").select("id,name").in("id", subjectIds);
          for (const s of subj ?? []) subjectMap[s.id] = s.name;
        }
        for (const a of assigns ?? []) {
          const sub = subs[a.id];
          let status: Row["status"] = "pending";
          if (sub?.status === "submitted") status = "submitted";
          else if (sub?.status === "graded") status = "graded";
          else if (a.due_at && new Date(a.due_at) < new Date()) status = "overdue";
          all.push({
            childId: c.learnerId,
            childName: c.firstName,
            id: a.id,
            title: a.title,
            subject: subjectMap[a.subject_id] ?? "Subject",
            due: a.due_at,
            status,
            score: sub?.score ?? null,
            max: a.max_score ?? null,
          });
        }
      }
      if (!cancelled) { setRows(all); setBusy(false); }
    })();
    return () => { cancelled = true; };
  }, [ctx]);

  return (
    <AppShell role="parent" title="Homework" subtitle="Every assignment, every child — all in one place.">
      {(loading || busy) && <div className="flex items-center gap-2 text-xs text-muted-foreground"><Loader2 className="h-3 w-3 animate-spin" /> Loading</div>}
      {!loading && !busy && rows.length === 0 && (
        <div className="rounded-2xl border border-dashed border-border bg-muted/30 p-10 text-center text-sm text-muted-foreground">No assignments yet.</div>
      )}
      <div className="grid gap-3 lg:grid-cols-2">
        {rows.map((r) => (
          <article key={r.id} className="grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-3 rounded-2xl border border-border bg-card p-4 shadow-soft">
            <span className={cn(
              "grid h-10 w-10 place-items-center rounded-xl",
              r.status === "overdue" ? "bg-destructive/15 text-destructive"
              : r.status === "graded" ? "bg-primary-soft text-primary"
              : r.status === "submitted" ? "bg-sky/15 text-sky"
              : "bg-muted text-foreground",
            )}>
              {r.status === "graded" || r.status === "submitted" ? <CheckCircle2 className="h-5 w-5" />
                : r.status === "overdue" ? <FileWarning className="h-5 w-5" /> : <ClipboardList className="h-5 w-5" />}
            </span>
            <div className="min-w-0">
              <div className="truncate text-sm font-semibold">{r.title}</div>
              <div className="truncate text-[11px] text-muted-foreground">{r.childName} · {r.subject}</div>
            </div>
            <div className="text-right text-[11px] text-muted-foreground">
              <div className="font-medium uppercase tracking-wide">{r.status}</div>
              <div>{r.due ? new Date(r.due).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "—"}</div>
              {r.score != null && r.max ? <div className="text-primary">{Math.round((Number(r.score) / Number(r.max)) * 100)}%</div> : null}
            </div>
          </article>
        ))}
      </div>
    </AppShell>
  );
}
